'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, GitBranch, Rss, ChevronLeft, ChevronRight, Settings, Bell, User, Activity, FlaskConical, ShieldCheck } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: string;
  badgeVariant?: 'alert' | 'info' | 'success';
}

const navItems: NavItem[] = [
  { label: 'Dashboard', href: '/', icon: LayoutDashboard },
  {
    label: 'Content Pipeline',
    href: '/content-pipeline-management',
    icon: GitBranch,
    badge: '3',
    badgeVariant: 'success',
  },
  {
    label: 'AI Trend Feed',
    href: '/ai-trend-feed',
    icon: Rss,
    badge: '7',
    badgeVariant: 'alert',
  },
  {
    label: 'E2E Test Runner',
    href: '/e2e-test-runner',
    icon: FlaskConical,
  },
];

const bottomItems: NavItem[] = [
  { label: 'Activity Log', href: '/activity-log', icon: Activity },
  { label: 'Notifications', href: '/notifications', icon: Bell, badge: '2', badgeVariant: 'info' },
  { label: 'Credentials', href: '/credentials', icon: ShieldCheck },
  { label: 'Settings', href: '/settings', icon: Settings },
  { label: 'Account', href: '/account', icon: User },
];

const badgeClass = {
  alert: 'bg-negative text-white',
  info: 'bg-[#833ab4] text-white',
  success: 'bg-positive text-white',
};

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const pathname = usePathname();

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  return (
    <aside
      className={`relative flex flex-col h-screen border-r border-border sidebar-transition shrink-0 ${
        collapsed ? 'w-16' : 'w-60'
      }`}
      style={{ background: 'linear-gradient(180deg, #0f0f1e 0%, #0a0a0f 100%)' }}
    >
      {/* Logo */}
      <div
        className={`flex items-center gap-3 px-4 py-5 border-b border-border ${
          collapsed ? 'justify-center px-0' : ''
        }`}
        style={{ background: 'linear-gradient(135deg, #833ab415 0%, #c1358415 50%, #f7773715 100%)' }}
      >
        <AppLogo size={32} />
        {!collapsed && (
          <div className="flex flex-col">
            <span
              className="text-sm font-semibold leading-tight"
              style={{
                background: 'linear-gradient(135deg, #833ab4, #c13584, #f77737)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              ContentPipeline
            </span>
            <span className="text-2xs font-mono" style={{ color: '#7070a0' }}>ops console</span>
          </div>
        )}
      </div>

      {/* Pipeline Status Dot */}
      {!collapsed && (
        <div className="mx-3 mt-3 mb-1 px-3 py-2 rounded-md border border-border flex items-center gap-2" style={{ background: '#1a1a2e' }}>
          <span className="w-2 h-2 rounded-full bg-positive pulse-green shrink-0" />
          <span className="text-xs font-mono" style={{ color: '#7070a0' }}>pipeline: running</span>
          <span className="ml-auto text-2xs font-mono" style={{ color: '#7070a0' }}>04:12</span>
        </div>
      )}

      {/* Nav Items */}
      <nav className="flex-1 px-2 py-2 space-y-1 overflow-y-auto">
        <div className={`mb-3 ${collapsed ? 'hidden' : ''}`}>
          <p className="px-2 text-2xs font-semibold uppercase tracking-widest mb-1" style={{ color: '#7070a0' }}>
            Pipeline
          </p>
        </div>
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          return (
            <Link
              key={`nav-${item.href}`}
              href={item.href}
              title={collapsed ? item.label : undefined}
              className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-150 ${
                collapsed ? 'justify-center px-0 w-10 mx-auto' : ''
              }`}
              style={
                active
                  ? {
                      background: 'linear-gradient(135deg, #833ab420 0%, #c1358420 50%, #f7773720 100%)',
                      border: '1px solid #c1358440',
                      color: '#e1306c',
                    }
                  : { color: '#7070a0' }
              }
              onMouseEnter={(e) => {
                if (!active) {
                  (e.currentTarget as HTMLElement).style.background = '#1e1e30';
                  (e.currentTarget as HTMLElement).style.color = '#fafafa';
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  (e.currentTarget as HTMLElement).style.background = '';
                  (e.currentTarget as HTMLElement).style.color = '#7070a0';
                }
              }}
            >
              <Icon size={18} className="shrink-0" />
              {!collapsed && (
                <span className="text-sm font-medium truncate">{item.label}</span>
              )}
              {!collapsed && item.badge && (
                <span
                  className={`ml-auto text-2xs font-semibold px-1.5 py-0.5 rounded-full ${
                    badgeClass[item.badgeVariant ?? 'info']
                  }`}
                >
                  {item.badge}
                </span>
              )}
              {collapsed && item.badge && (
                <span
                  className={`absolute -top-1 -right-1 w-4 h-4 text-2xs font-semibold flex items-center justify-center rounded-full ${
                    badgeClass[item.badgeVariant ?? 'info']
                  }`}
                >
                  {item.badge}
                </span>
              )}
              {collapsed && (
                <span className="absolute left-full ml-3 px-2 py-1 border border-border text-foreground text-xs rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-150" style={{ background: '#1a1a2e' }}>
                  {item.label}
                </span>
              )}
            </Link>
          );
        })}

        <div className={`pt-4 pb-1 ${collapsed ? 'hidden' : ''}`}>
          <p className="px-2 text-2xs font-semibold uppercase tracking-widest mb-1" style={{ color: '#7070a0' }}>
            System
          </p>
        </div>
        {bottomItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          return (
            <Link
              key={`nav-bottom-${item.label}`}
              href={item.href}
              title={collapsed ? item.label : undefined}
              className={`group relative flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-150 ${
                collapsed ? 'justify-center px-0 w-10 mx-auto' : ''
              }`}
              style={
                active
                  ? {
                      background: 'linear-gradient(135deg, #833ab420 0%, #c1358420 50%, #f7773720 100%)',
                      border: '1px solid #c1358440',
                      color: '#e1306c',
                    }
                  : { color: '#7070a0' }
              }
              onMouseEnter={(e) => {
                if (!active) {
                  (e.currentTarget as HTMLElement).style.background = '#1e1e30';
                  (e.currentTarget as HTMLElement).style.color = '#fafafa';
                }
              }}
              onMouseLeave={(e) => {
                if (!active) {
                  (e.currentTarget as HTMLElement).style.background = '';
                  (e.currentTarget as HTMLElement).style.color = '#7070a0';
                }
              }}
            >
              <Icon size={18} className="shrink-0" />
              {!collapsed && (
                <span className="text-sm font-medium truncate">{item.label}</span>
              )}
              {!collapsed && item.badge && (
                <span
                  className={`ml-auto text-2xs font-semibold px-1.5 py-0.5 rounded-full ${
                    badgeClass[item.badgeVariant ?? 'info']
                  }`}
                >
                  {item.badge}
                </span>
              )}
              {collapsed && item.badge && (
                <span
                  className={`absolute -top-1 -right-1 w-4 h-4 text-2xs font-semibold flex items-center justify-center rounded-full ${
                    badgeClass[item.badgeVariant ?? 'info']
                  }`}
                >
                  {item.badge}
                </span>
              )}
              {collapsed && (
                <span className="absolute left-full ml-3 px-2 py-1 border border-border text-foreground text-xs rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50 transition-opacity duration-150" style={{ background: '#1a1a2e' }}>
                  {item.label}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Collapse Toggle */}
      <div className="border-t border-border p-2">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className={`flex items-center gap-2 w-full px-3 py-2 rounded-md transition-all duration-150 ${
            collapsed ? 'justify-center' : ''
          }`}
          style={{ color: '#7070a0' }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.background = '#1e1e30';
            (e.currentTarget as HTMLElement).style.color = '#fafafa';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.background = '';
            (e.currentTarget as HTMLElement).style.color = '#7070a0';
          }}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          {!collapsed && <span className="text-xs">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}